import React, { useState } from 'react';
import { ChevronDown, BookOpen, Brain, Video, Menu, X } from 'lucide-react';

const SmartEnglishLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="text-2xl font-bold text-blue-600">
                SBA<span className="text-gray-800"> ULIS</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200">
                About
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200">
                Contact
              </a>
              
              {/* Login Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                  className="flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <span>Login</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isLoginDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                    <a
                      href="/student/login"
                      className="flex items-center px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                    >
                      <span className="mr-2">🧑‍🎓</span>
                      Student
                    </a>
                    <a
                      href="/teacher/login"
                      className="flex items-center px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                    >
                      <span className="mr-2">👩‍🏫</span>
                      Teacher
                    </a>
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-blue-600"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col space-y-4">
                <a href="#" className="text-gray-600 hover:text-blue-600">About</a>
                <a href="#" className="text-gray-600 hover:text-blue-600">Contact</a>
                <div className="flex flex-col space-y-2 pt-2 border-t border-gray-100">
                  <a href="/student/login" className="flex items-center text-gray-700 hover:text-blue-600">
                    <span className="mr-2">🧑‍🎓</span>
                    Login - Student
                  </a>
                  <a href="/teacher/login" className="flex items-center text-gray-700 hover:text-blue-600">
                    <span className="mr-2">👩‍🏫</span>
                    Login - Teacher
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-br from-blue-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="mb-12 lg:mb-0">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Welcome to{' '}
                <span className="text-blue-600">SBA</span>
                <span className="text-gray-800"> ULIS</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Vietnam's first AI-powered English test preparation and grading platform.
              </p>

              <div className="space-y-4 mb-10">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🎓</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">English test preparation system with AI-powered automatic grading</h3>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🧠</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Detailed explanations after every question</h3>
                    <p className="text-gray-600">Instant reading feedback with detailed explanations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">🎥</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Interactive teacher video guidance</h3>
                    <p className="text-gray-600">Creating an offline learning experience</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">📝</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Easy and intuitive learning and testing</h3>
                    <p className="text-gray-600">Time-saving for both students and teachers</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/student/login"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span className="mr-2">🧑‍🎓</span>
                  I'm a Student
                </a>
                <a
                  href="/teacher/login"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-600 bg-white border-2 border-blue-600 rounded-xl hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span className="mr-2">👩‍🏫</span>
                  I'm a Teacher
                </a>
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="relative">
              <div className="relative bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl p-8 shadow-2xl">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900">AI Teacher</h4>
                      <p className="text-sm text-gray-500">Currently teaching</p>
                    </div>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-32 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-600 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-2xl text-white">👩‍🏫</span>
                      </div>
                      <p className="text-sm text-gray-600">AI Teacher Video</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Smart learning experience with advanced AI technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Standardized Test System
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Every question is crafted by teachers according to international standards, ensuring high quality and accuracy.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Brain className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                AI Grading & Explanations
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Smart feedback without revealing answers. Writing is evaluated by intelligent AI models using rubrics.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Video className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Teacher Video Guidance
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Creating real interaction experience, visual teacher guidance videos help students understand better.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">
              SBA<span className="text-blue-400"> ULIS</span>
            </div>
            <div className="space-y-2 text-gray-400">
              <p>© 2025 sba.ulis.vn</p>
              <p>Contact: contact@sba.ulis.vn.ai</p>
              <div className="flex justify-center space-x-6 mt-4">
                <a href="#" className="hover:text-blue-400 transition-colors duration-200">
                  Privacy Policy
                </a>
                <span>|</span>
                <a href="#" className="hover:text-blue-400 transition-colors duration-200">
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartEnglishLanding;